import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, FileText, ShieldAlert, DollarSign, Check, ChevronsUpDown, X, Settings2, Layers, History } from "lucide-react";
import { cn } from "@/lib/utils";
import { CancellationPoliciesCard } from "@/components/admin/cancellation-policies-card";
import { InvoiceSettingsDialog } from "@/components/invoice-settings-dialog";
import { LateFeeSettingsDialog } from "@/components/late-fee-settings-dialog";
import { BookingSettingsCard } from "@/components/admin/booking-settings-card";
import { InvoiceChangeLogCard } from "@/components/admin/invoice-change-log-card";
import type { ClientTypeRecord } from "@shared/schema";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

interface PreviewResponse {
  input: {
    clientId: string | null;
    clientTypeId: string | null;
    clientTypeName: string | null;
    state: string | null;
  };
  cancellation: {
    policy: { id: string; name: string; body: string; isDefault?: boolean } | null;
    source: string;
  };
  lateFees: {
    tiers: Array<{
      id: string;
      name: string;
      clientType: string;
      daysAfterEvent: number;
      feeType: "flat" | "percent";
      feeAmount: string;
      gracePeriodDays: number | null;
      recurringIntervalDays: number | null;
      isRecurring: boolean;
    }>;
    tiersSource: string;
    stateException: {
      state: string;
      maxFeeAmount: string | null;
      maxFeePercent: string | null;
    } | null;
    stateExceptionSource: string;
  };
  invoiceSettings: {
    settings: {
      applyLateFees: boolean;
      paymentTerms: string;
      disclaimerText: string;
      enableDeposits: boolean;
      depositMode: "percent" | "flat";
      depositPercentage: number;
      depositFlatCents: number;
      depositForcedByAdmin: boolean;
    } | null;
    source: string;
  };
}

function SourceBadge({ source }: { source: string }) {
  const variant: "default" | "secondary" | "outline" =
    source.startsWith("client-type") ? "default"
    : source === "workspace-default" ? "secondary"
    : "outline";
  return (
    <Badge variant={variant} className="font-mono text-xs" data-testid={`badge-source-${source}`}>
      {source}
    </Badge>
  );
}

interface ClientLite {
  id: string;
  accountName: string | null;
  city: string | null;
  state: string | null;
  clientType: string | null;
}

function PreviewCard() {
  const [clientTypeId, setClientTypeId] = useState<string>("");
  const [stateCode, setStateCode] = useState<string>("");
  const [clientId, setClientId] = useState<string>("");
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  const { data: clients = [], isLoading: clientsLoading } = useQuery<ClientLite[]>({
    queryKey: ["/api/clients", "lookup"],
  });

  const selectedClient = clients.find((c) => c.id === clientId) ?? null;

  const params = new URLSearchParams();
  if (clientTypeId) params.set("clientTypeId", clientTypeId);
  if (stateCode) params.set("state", stateCode);
  if (clientId.trim()) params.set("clientId", clientId.trim());

  const enabled = Boolean(clientTypeId || clientId.trim());
  const { data, isFetching } = useQuery<PreviewResponse>({
    queryKey: ["/api/invoice-policies/preview", params.toString()],
    queryFn: async () => {
      const res = await fetch(`/api/invoice-policies/preview?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load preview");
      return res.json();
    },
    enabled,
  });

  return (
    <Card data-testid="card-invoice-policy-preview">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">Preview Effective Policy</CardTitle>
        </div>
        <CardDescription>
          Pick a client type (and optionally a state or specific client) to see exactly which
          cancellation, late-fee, and invoice-settings rows will apply at snapshot time.
          Read-only — nothing here changes data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="preview-ct" className="text-xs">Client Type</Label>
            <Select
              value={clientTypeId || "__none__"}
              onValueChange={(v) => setClientTypeId(v === "__none__" ? "" : v)}
              disabled={Boolean(clientId)}
            >
              <SelectTrigger id="preview-ct" data-testid="select-preview-client-type">
                <SelectValue placeholder="Select client type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Any / clear —</SelectItem>
                {clientTypes.map((ct) => (
                  <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {clientId && (
              <p className="text-xs text-muted-foreground mt-1">
                Locked — using selected client's type.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="preview-state" className="text-xs">State (optional)</Label>
            <Select value={stateCode || "__none__"} onValueChange={(v) => setStateCode(v === "__none__" ? "" : v)}>
              <SelectTrigger id="preview-state" data-testid="select-preview-state">
                <SelectValue placeholder="Any state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Any state —</SelectItem>
                {US_STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Specific client (optional)</Label>
            <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                  data-testid="button-preview-client-picker"
                  disabled={clientsLoading}
                >
                  <span className="truncate">
                    {selectedClient
                      ? `${selectedClient.accountName ?? "Unnamed"}${selectedClient.state ? ` · ${selectedClient.state}` : ""}`
                      : clientsLoading ? "Loading…" : "Pick a client…"}
                  </span>
                  {selectedClient ? (
                    <X
                      className="ml-2 h-4 w-4 opacity-60 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClientId("");
                      }}
                      data-testid="button-clear-preview-client"
                    />
                  ) : (
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by account name…" />
                  <CommandList>
                    <CommandEmpty>No clients found.</CommandEmpty>
                    <CommandGroup>
                      {clients.slice(0, 200).map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.accountName ?? ""} ${c.city ?? ""} ${c.state ?? ""}`}
                          onSelect={() => {
                            setClientId(c.id);
                            setClientPickerOpen(false);
                          }}
                          data-testid={`option-preview-client-${c.id}`}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              clientId === c.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <div className="flex flex-col">
                            <span className="text-sm">{c.accountName ?? "Unnamed"}</span>
                            <span className="text-xs text-muted-foreground">
                              {[c.clientType, c.city, c.state].filter(Boolean).join(" · ")}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {!enabled && (
          <p className="text-sm text-muted-foreground" data-testid="text-preview-empty">
            Select a client type or a specific client to preview the effective policy. Adding a state shows the matching state-cap exception.
          </p>
        )}

        {enabled && isFetching && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Resolving…
          </div>
        )}

        {data && !isFetching && (
          <div className="space-y-4" data-testid="container-preview-results">
            <section className="rounded-md border p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">Cancellation Policy</h4>
                </div>
                <SourceBadge source={data.cancellation.source} />
              </div>
              {data.cancellation.policy ? (
                <div data-testid="text-preview-cancellation">
                  <div className="font-medium text-sm">{data.cancellation.policy.name}</div>
                  <div className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">
                    {data.cancellation.policy.body}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No policy resolved — bookings will snapshot empty terms.</div>
              )}
            </section>

            <section className="rounded-md border p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">Late Fee Tiers</h4>
                </div>
                <SourceBadge source={data.lateFees.tiersSource} />
              </div>
              {data.lateFees.tiers.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="text-preview-no-tiers">
                  No active tiers for this client type.
                </div>
              ) : (
                <ul className="space-y-1.5 text-sm" data-testid="list-preview-tiers">
                  {data.lateFees.tiers.map((t) => {
                    const feeStr = t.feeType === "percent"
                      ? `${t.feeAmount}%`
                      : `$${t.feeAmount}`;
                    return (
                      <li key={t.id} className="flex justify-between gap-2 items-start">
                        <div className="flex flex-col">
                          <span className="font-medium">{t.name}</span>
                          <span className="text-xs text-muted-foreground">
                            Triggers {t.daysAfterEvent}d after event
                            {t.gracePeriodDays ? ` · ${t.gracePeriodDays}d grace` : ""}
                            {t.isRecurring && t.recurringIntervalDays
                              ? ` · recurring every ${t.recurringIntervalDays}d`
                              : ""}
                          </span>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs shrink-0">
                          {feeStr} {t.feeType === "percent" ? "of balance" : "flat"}
                        </Badge>
                      </li>
                    );
                  })}
                </ul>
              )}
              {data.lateFees.stateException && (
                <div className="mt-3 pt-3 border-t flex items-start justify-between gap-2">
                  <div className="text-sm" data-testid="text-preview-state-exception">
                    <div className="font-medium">State exception ({data.lateFees.stateException.state})</div>
                    <div className="text-xs text-muted-foreground">
                      {data.lateFees.stateException.maxFeeAmount
                        ? `Capped at $${data.lateFees.stateException.maxFeeAmount}`
                        : data.lateFees.stateException.maxFeePercent
                          ? `Capped at ${data.lateFees.stateException.maxFeePercent}%`
                          : "Active"}
                    </div>
                  </div>
                  <SourceBadge source={data.lateFees.stateExceptionSource} />
                </div>
              )}
            </section>

            <section className="rounded-md border p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">General Invoice Settings</h4>
                </div>
                <SourceBadge source={data.invoiceSettings.source} />
              </div>
              {data.invoiceSettings.settings ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="grid-preview-invoice-settings">
                  <dt className="text-muted-foreground">Apply late fees</dt>
                  <dd>{data.invoiceSettings.settings.applyLateFees ? "Yes" : "No"}</dd>
                  <dt className="text-muted-foreground">Deposits</dt>
                  <dd>
                    {data.invoiceSettings.settings.enableDeposits
                      ? data.invoiceSettings.settings.depositMode === "flat"
                        ? `$${(data.invoiceSettings.settings.depositFlatCents / 100).toFixed(2)} flat`
                        : `${data.invoiceSettings.settings.depositPercentage}%`
                      : "Disabled"}
                    {data.invoiceSettings.settings.depositForcedByAdmin ? " (forced)" : ""}
                  </dd>
                  <dt className="text-muted-foreground">Payment terms</dt>
                  <dd className="truncate">{data.invoiceSettings.settings.paymentTerms || "—"}</dd>
                  <dt className="text-muted-foreground">Disclaimer</dt>
                  <dd className="truncate">{data.invoiceSettings.settings.disclaimerText || "—"}</dd>
                </dl>
              ) : (
                <div className="text-sm text-muted-foreground">No settings row — invoice will use factory defaults.</div>
              )}
            </section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface PaymentOptionsSettings {
  enableInvoicePayments?: boolean;
  enableDepositCollection?: boolean;
  depositPercentage?: number;
}

function PaymentOptionsCard() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useQuery<PaymentOptionsSettings>({
    queryKey: ["/api/payment-settings"],
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<PaymentOptionsSettings>) => {
      const res = await apiRequest("PATCH", "/api/payment-settings", updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/payment-settings"] });
      toast({
        title: "Settings Saved",
        description: "Payment options have been updated.",
      });
    },
    onError: () => {
      toast({
        title: "Save Failed",
        description: "Could not update payment options. Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <Card data-testid="card-payment-options">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">Payment Options</CardTitle>
        </div>
        <CardDescription>
          Workspace-wide toggles for accepting online invoice payments and
          collecting deposits at booking time. These used to live under the
          old Payment Portal page — they now live here so every invoice-shaped
          setting is in one place.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Invoice Payments</Label>
                <p className="text-sm text-muted-foreground">Allow clients to pay invoices online</p>
              </div>
              <Switch
                checked={settings?.enableInvoicePayments || false}
                onCheckedChange={(checked) => updateMutation.mutate({ enableInvoicePayments: checked })}
                data-testid="switch-invoice-payments"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Deposit Collection</Label>
                <p className="text-sm text-muted-foreground">Collect deposits during booking</p>
              </div>
              <Switch
                checked={settings?.enableDepositCollection || false}
                onCheckedChange={(checked) => updateMutation.mutate({ enableDepositCollection: checked })}
                data-testid="switch-deposit-collection"
              />
            </div>

            {settings?.enableDepositCollection && (
              <div className="flex items-center gap-4 pl-4">
                <Label>Deposit Percentage</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={settings?.depositPercentage || 50}
                    onChange={(e) => updateMutation.mutate({ depositPercentage: parseInt(e.target.value) })}
                    className="w-20"
                    data-testid="input-deposit-percentage"
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface InvoicePoliciesPanelProps {
  initialTab?: "settings" | "cancellation" | "lateFees" | "bookings";
}

export function InvoicePoliciesPanel({ initialTab = "settings" }: InvoicePoliciesPanelProps) {
  const [tab, setTab] = useState<string>(initialTab);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <p className="text-sm text-muted-foreground">
        One place for invoice payment options, general invoice settings, cancellation policies,
        and late-fee schedules. Each section is keyed per client type so different audiences get
        the right policy on their invoices and bookings.
      </p>

      <PreviewCard />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-invoice-settings">
            <FileText className="h-4 w-4 mr-1" /> General Settings
          </TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings-multi-talent">
            <Layers className="h-4 w-4 mr-1" /> Bookings & Multi-Talent
          </TabsTrigger>
          <TabsTrigger value="cancellation" data-testid="tab-cancellation-policies">
            <ShieldAlert className="h-4 w-4 mr-1" /> Cancellation
          </TabsTrigger>
          <TabsTrigger value="lateFees" data-testid="tab-late-fees">
            <DollarSign className="h-4 w-4 mr-1" /> Late Fees
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-invoice-logs">
            <History className="h-4 w-4 mr-1" /> Invoice Logs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="settings" className="mt-4 space-y-4">
          <PaymentOptionsCard />
          <InvoiceSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
        </TabsContent>
        <TabsContent value="bookings" className="mt-4">
          <BookingSettingsCard />
        </TabsContent>
        <TabsContent value="cancellation" className="mt-4">
          <CancellationPoliciesCard />
        </TabsContent>
        <TabsContent value="lateFees" className="mt-4">
          <LateFeeSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <InvoiceChangeLogCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
